import type { ReactNode } from 'react'
import { BRAND } from './brand.js'
import { ICON_NAMES, Icon } from './icons.js'
import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  List,
  ListRow,
  PageHeader,
  Radio,
  Select,
  Sheet,
  Skeleton,
  Stat,
  StatusDot,
  Textarea,
  Toolbar,
  type Tone,
} from './primitives.js'
import {
  CONTROL_TOKENS,
  DESIGN_TOKENS,
  FOCUS_TOKENS,
  MOTION_TOKENS,
  RADIUS_TOKENS,
  SPACE_TOKENS,
  TYPOGRAPHY_TOKENS,
} from './tokens.js'
import './reference.css'

/**
 * Test-only reference page: every token and every primitive on one screen, rendered twice — once
 * forced light, once forced dark — so the system can be reviewed without a route or a build step.
 *
 * Nothing in the app imports this module. It is exported for the dev/test use described in
 * `packages/ui/README.md`, and `reference.test.tsx` fails if a token or icon stops being shown here.
 */

const TONES: readonly Tone[] = ['neutral', 'accent', 'success', 'warning', 'danger', 'info']

const COLOR_GROUPS: readonly { readonly title: string; readonly tokens: readonly string[] }[] = [
  { title: 'Surfaces', tokens: DESIGN_TOKENS.surface },
  { title: 'Text', tokens: DESIGN_TOKENS.text },
  { title: 'Borders', tokens: DESIGN_TOKENS.border },
  { title: 'Accent and quiet', tokens: DESIGN_TOKENS.accent },
  { title: 'Deep (the second primary action)', tokens: DESIGN_TOKENS.deep },
  { title: 'Status', tokens: DESIGN_TOKENS.status },
  { title: 'Focus and loading', tokens: DESIGN_TOKENS.focus },
  { title: 'Disabled', tokens: DESIGN_TOKENS.disabled },
  { title: 'Signers (paper, then app surfaces)', tokens: DESIGN_TOKENS.signer },
  { title: 'Chart series', tokens: DESIGN_TOKENS.chart },
]

function Section({
  title,
  description,
  children,
}: {
  readonly title: string
  readonly description?: string
  readonly children: ReactNode
}) {
  return (
    <section className="op-reference__section">
      <h3 className="op-reference__section-title">{title}</h3>
      {description === undefined ? null : (
        <p className="op-reference__section-description">{description}</p>
      )}
      <div className="op-reference__section-body">{children}</div>
    </section>
  )
}

function TypeSamples({
  tokens,
  property,
  sample,
}: {
  readonly tokens: readonly string[]
  readonly property: 'fontSize' | 'lineHeight' | 'fontWeight' | 'letterSpacing' | 'fontFamily'
  readonly sample: string
}) {
  return (
    <>
      {tokens.map((token) => (
        <div className="op-reference__type-row" key={token}>
          <span className="op-reference__token">{token}</span>
          <span style={{ [property]: `var(${token})` }}>{sample}</span>
        </div>
      ))}
    </>
  )
}

function ColorSwatches() {
  return (
    <>
      {COLOR_GROUPS.map((group) => (
        <Section title={group.title} key={group.title}>
          <div className="op-reference__swatches">
            {group.tokens.map((token) => (
              <div className="op-reference__swatch" key={token}>
                <span
                  className="op-reference__swatch-chip"
                  style={{ background: `var(${token})` }}
                />
                <span className="op-reference__token">{token}</span>
              </div>
            ))}
          </div>
        </Section>
      ))}
    </>
  )
}

function TokenScales() {
  return (
    <>
      <Section title="Type scale" description="Sizes, line heights, weights and tracking.">
        <TypeSamples
          tokens={TYPOGRAPHY_TOKENS.filter((token) => token.includes('font-size'))}
          property="fontSize"
          sample="Agreement ready to sign"
        />
        <TypeSamples
          tokens={TYPOGRAPHY_TOKENS.filter((token) => token.includes('line-height'))}
          property="lineHeight"
          sample="Signers can review every page before they consent to the agreement."
        />
        <TypeSamples
          tokens={TYPOGRAPHY_TOKENS.filter((token) => token.includes('font-weight'))}
          property="fontWeight"
          sample="Signature requested"
        />
        <TypeSamples
          tokens={TYPOGRAPHY_TOKENS.filter((token) => token.includes('tracking'))}
          property="letterSpacing"
          sample="Signed and sealed"
        />
        <TypeSamples
          tokens={TYPOGRAPHY_TOKENS.filter((token) => token.includes('font-family'))}
          property="fontFamily"
          sample="Contract-2026-09-23.pdf"
        />
      </Section>
      <Section
        title="Spacing scale"
        description="One 4px-rooted scale, from hairline to page rhythm."
      >
        {SPACE_TOKENS.map((token) => (
          <div className="op-reference__space-row" key={token}>
            <span className="op-reference__token">{token}</span>
            <span className="op-reference__space-bar" style={{ inlineSize: `var(${token})` }} />
          </div>
        ))}
      </Section>
      <Section title="Radii" description="Controls, cards, sheets and pills.">
        <div className="op-reference__row">
          {RADIUS_TOKENS.map((token) => (
            <div className="op-reference__swatch" key={token}>
              <span
                className="op-reference__radius-box"
                style={{ borderRadius: `var(${token})` }}
              />
              <span className="op-reference__token">{token}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Elevation" description="Raised surfaces and the modal sheet.">
        <div className="op-reference__grid">
          {DESIGN_TOKENS.elevation.map((token) => (
            <div
              className="op-reference__elevation-card"
              style={{ boxShadow: `var(${token})` }}
              key={token}
            >
              <span className="op-reference__token">{token}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section
        title="Motion"
        description="Transitions are 120–160 ms ease-out; under prefers-reduced-motion the durations become 0 ms and the shimmer stops."
      >
        <div className="op-reference__row">
          <span className="op-reference__motion-demo" />
          <span className="op-reference__token">{MOTION_TOKENS.join('  ')}</span>
        </div>
        <div className="op-reference__row">
          <Skeleton width="lg" />
          <Skeleton size="title" width="md" />
          <Skeleton size="circle" />
        </div>
        <div className="op-reference__row">
          <span className="op-reference__token">{CONTROL_TOKENS.join('  ')}</span>
        </div>
        <div className="op-reference__row">
          <span className="op-reference__token">{FOCUS_TOKENS.join('  ')}</span>
        </div>
      </Section>
    </>
  )
}

function Buttons() {
  return (
    <Section title="Button" description="Variants, sizes, icons, loading and disabled states.">
      <div className="op-reference__row">
        <Button variant="primary">Send for signature</Button>
        <Button variant="deep">Send</Button>
        <Button variant="secondary">Save draft</Button>
        <Button variant="quiet">Cancel</Button>
        <Button variant="danger">Delete</Button>
      </div>
      <div className="op-reference__row">
        <Button size="sm">Small</Button>
        <Button size="md">Medium</Button>
        <Button size="lg">Large</Button>
      </div>
      <div className="op-reference__row">
        <Button variant="primary" loading>
          Sending
        </Button>
        <Button disabled>Unavailable</Button>
        <Button leadingIcon={<Icon name="upload" />}>Import</Button>
        <Button trailingIcon={<Icon name="chevron-right" />}>Continue</Button>
        <Button iconOnly aria-label="Refresh list">
          <Icon name="refresh" />
        </Button>
        <Button variant="primary" iconOnly loading aria-label="Sending" />
      </div>
      <div className="op-reference__grid">
        <Button variant="primary" fullWidth leadingIcon={<Icon name="send" />}>
          Full width
        </Button>
      </div>
    </Section>
  )
}

function Badges() {
  return (
    <Section title="Badge and StatusDot" description="Compact state, with and without a label.">
      <div className="op-reference__row">
        {TONES.map((tone) => (
          <Badge tone={tone} key={tone}>
            {tone}
          </Badge>
        ))}
      </div>
      <div className="op-reference__row">
        {TONES.map((tone) => (
          <span className="op-reference__row" key={tone}>
            <StatusDot tone={tone} />
            <span className="op-reference__token">{tone}</span>
          </span>
        ))}
      </div>
      <div className="op-reference__row">
        <StatusDot tone="success" label="Signature complete" />
        <span className="op-reference__token">StatusDot with an accessible label</span>
      </div>
    </Section>
  )
}

function Cards() {
  return (
    <Section title="Card" description="Plain, raised and inset, with an optional header.">
      <div className="op-reference__grid">
        <Card
          title="Recent agreements"
          description="Everything you sent this month."
          actions={<Button size="sm">Refresh</Button>}
        >
          <List>
            <ListRow
              title="Service agreement.pdf"
              meta="Sent 2 days ago"
              trailing={<Badge tone="accent">Awaiting</Badge>}
            />
            <ListRow
              title="NDA.pdf"
              meta="Signed yesterday"
              trailing={<Badge tone="success">Signed</Badge>}
            />
          </List>
        </Card>
        <Card variant="raised" title="Raised">
          <p>Raised surfaces use the elevation tokens instead of a border.</p>
        </Card>
        <Card variant="inset" title="Inset">
          <p>Inset surfaces sit below the page for grouping and empty areas.</p>
        </Card>
      </div>
    </Section>
  )
}

function HeaderAndEmpty() {
  return (
    <>
      <Section title="PageHeader" description="Title, description and the page's primary actions.">
        <PageHeader
          title="Agreements"
          description="Every agreement you have sent, newest first."
          actions={
            <Button variant="primary" leadingIcon={<Icon name="plus" />}>
              New agreement
            </Button>
          }
        />
      </Section>
      <Section title="EmptyState" description="Icon, title, description and one action.">
        <EmptyState
          icon={<Icon name="document" size={24} />}
          title="No agreements yet"
          description="Send your first document for signature."
          action={<Button variant="primary">Send a document</Button>}
        />
      </Section>
    </>
  )
}

function Fields() {
  return (
    <Section
      title="Field, Input, Select, Textarea"
      description="Real labels, hints and error wiring."
    >
      <div className="op-reference__grid">
        <Field
          id="reference-email"
          label="Recipient email"
          hint="We send the signing link here."
          required
        >
          <Input type="email" placeholder="name@example.com" />
        </Field>
        <Field id="reference-locale" label="Signing language">
          <Select defaultValue="en">
            <option value="en">English</option>
            <option value="es">Spanish</option>
          </Select>
        </Field>
        <Field id="reference-due" label="Due date" error="Enter a date in the future.">
          <Input defaultValue="2020-01-01" />
        </Field>
        <Field id="reference-note" label="Message to signers" hint="Optional, shown in the email.">
          <Textarea placeholder="Please review and sign." />
        </Field>
        <Field id="reference-locked" label="Reference">
          <Input defaultValue="AGR-1042" disabled />
        </Field>
        <Field id="reference-invalid" label="Amount">
          <Input invalid defaultValue="0" />
        </Field>
      </div>
    </Section>
  )
}

/** Each panel names its own radio group, so the light and dark copies don't act as one group. */
function Choices({ theme }: { readonly theme: 'light' | 'dark' }) {
  const group = `reference-delivery-${theme}`
  return (
    <Section title="Checkbox and Radio" description="Native inputs on the tokens: every state.">
      <div className="op-reference__grid">
        <div className="op-reference__stack">
          <Checkbox label="Send me a copy" />
          <Checkbox label="Remember this signature" defaultChecked />
          <Checkbox
            id={`reference-consent-${theme}`}
            label="I agree to sign electronically"
            hint="Required before you can sign."
            invalid
          />
          <Checkbox label="Locked option" disabled />
          <Checkbox label="Locked and on" disabled defaultChecked />
        </div>
        <fieldset className="op-reference__stack">
          <legend>Delivery</legend>
          <Radio name={group} label="Share the link myself" defaultChecked />
          <Radio name={group} label="Send with email" />
          <Radio name={group} label="Unavailable here" disabled />
        </fieldset>
      </div>
    </Section>
  )
}

function Lists() {
  return (
    <Section title="List and ListRow" description="Static rows, clickable rows and linked rows.">
      <List>
        <ListRow
          leading={<Icon name="document" />}
          title="Service agreement.pdf"
          meta="3 pages · sent to ana@example.com"
          trailing={<StatusDot tone="accent" label="Awaiting signature" />}
        />
        <ListRow
          leading={<Icon name="clock" />}
          title="NDA.pdf"
          meta="Signed yesterday"
          trailing={<Icon name="chevron-right" />}
          onClick={() => undefined}
        />
        <ListRow
          leading={<Icon name="link" />}
          title="Privacy policy"
          meta={`${BRAND.domain}/privacy`}
          href="https://example.com"
        />
      </List>
    </Section>
  )
}

function CalloutsAndStats() {
  return (
    <>
      <Section
        title="Callout"
        description="Info and success announce politely; warning and danger assertively."
      >
        <Callout tone="info" title="Stored on this device">
          Documents stay local unless you send them.
        </Callout>
        <Callout tone="success" title="Signature complete">
          The signed copy is ready to download.
        </Callout>
        <Callout tone="warning" title="Check the due date">
          The date is in the past.
        </Callout>
        <Callout tone="danger" title="Upload failed">
          The file could not be read. Try again.
        </Callout>
      </Section>
      <Section title="Stat" description="Label, value and hint.">
        <div className="op-reference__grid">
          <Stat label="Sent" value="128" hint="Last 30 days" />
          <Stat label="Signed" value="94" hint="73% completion" />
          <Stat label="Awaiting" value="34" hint="3 expire this week" />
        </div>
      </Section>
    </>
  )
}

function ToolbarAndSkeleton() {
  return (
    <>
      <Section title="Toolbar" description="A labelled group of filters and actions.">
        <Toolbar label="Agreement filters">
          <Field id="reference-search" label="Search">
            <Input type="search" placeholder="Search agreements" />
          </Field>
          <Button leadingIcon={<Icon name="filter" />}>Filter</Button>
          <Button variant="quiet" leadingIcon={<Icon name="chart" />}>
            Insights
          </Button>
        </Toolbar>
      </Section>
      <Section
        title="Skeleton"
        description="Loading placeholders, hidden from assistive tech unless labelled."
      >
        <Skeleton lines={3} width="full" />
        <Skeleton label="Loading agreements" size="title" width="md" />
        <Skeleton size="block" width="full" />
      </Section>
    </>
  )
}

function SheetDemo() {
  return (
    <Section
      title="Sheet"
      description="Modal dialog: labelled, Escape closes it, backdrop click closes it."
    >
      <div className="op-reference__sheet-demo">
        <Sheet open title="More" onClose={() => undefined}>
          <div className="op-reference__section-body">
            <List>
              <ListRow
                leading={<Icon name="settings" />}
                title="Settings"
                onClick={() => undefined}
              />
              <ListRow
                leading={<Icon name="plug" />}
                title="Connections"
                onClick={() => undefined}
              />
              <ListRow leading={<Icon name="shield" />} title="Privacy" onClick={() => undefined} />
            </List>
            <Button variant="primary" fullWidth>
              Close
            </Button>
          </div>
        </Sheet>
      </div>
    </Section>
  )
}

function Icons() {
  return (
    <Section title="Icons" description="Inline SVG, currentColor, 1.5px stroke on a 24px grid.">
      <div className="op-reference__icons">
        {ICON_NAMES.map((name) => (
          <div className="op-reference__icon-cell" key={name}>
            <Icon name={name} />
            <span className="op-reference__token">{name}</span>
          </div>
        ))}
      </div>
      <div className="op-reference__row">
        <Icon name="document" size={24} />
        <Icon name="send" size={24} />
        <Icon name="shield" size={24} />
        <Icon name="chart" size={24} />
        <span className="op-reference__token">size=24</span>
      </div>
    </Section>
  )
}

function Gallery({ theme }: { readonly theme: 'light' | 'dark' }) {
  return (
    <>
      <ColorSwatches />
      <TokenScales />
      <Buttons />
      <Badges />
      <Cards />
      <HeaderAndEmpty />
      <Fields />
      <Choices theme={theme} />
      <Lists />
      <CalloutsAndStats />
      <ToolbarAndSkeleton />
      <SheetDemo />
      <Icons />
    </>
  )
}

export function DesignReference() {
  return (
    <div className="op-reference">
      <header className="op-reference__header">
        <p className="op-reference__brand">{BRAND.product_name} design foundation</p>
        <h1 className="op-reference__title">Design reference</h1>
        <p className="op-reference__lead">
          Every token and primitive, rendered twice with the theme forced: the left panel is always
          light, the right panel is always dark, whatever the operating system prefers. Colours come
          from <code>tokens.css</code>; nothing on this page restates them.
        </p>
      </header>
      <div className="op-reference__themes">
        <div className="op-reference__panel" data-theme="light">
          <p className="op-reference__panel-label">Light theme</p>
          <Gallery theme="light" />
        </div>
        <div className="op-reference__panel" data-theme="dark">
          <p className="op-reference__panel-label">Dark theme</p>
          <Gallery theme="dark" />
        </div>
      </div>
    </div>
  )
}
